import LogRocket from "logrocket";

type MonitoringUser = {
  id: string;
  email?: string;
  name?: string;
};

type CaptureOptions = {
  level?: "fatal" | "error" | "warning" | "info" | "debug";
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
};

type SentryScopeLike = {
  setLevel: (level: CaptureOptions["level"]) => void;
  setTag: (key: string, value: string) => void;
  setExtras: (extras: Record<string, unknown>) => void;
  setFingerprint: (fingerprint: string[]) => void;
};

type SentryModuleLike = {
  init: (options: Record<string, unknown>) => void;
  setUser: (user: { id?: string; email?: string; username?: string } | null) => void;
  captureException: (error: unknown) => void;
  withScope?: (callback: (scope: SentryScopeLike) => void) => void;
  captureMessage?: (message: string) => void;
};

let sentryModule: SentryModuleLike | null = null;
let sentryLoadAttempted = false;
let monitoringInitialized = false;

const REDACTED_KEYS = [
  "authorization",
  "apikey",
  "api_key",
  "token",
  "access_token",
  "refresh_token",
  "password",
  "secret",
];

const LOGROCKET_STRING_LIMIT = 200;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const sanitizeMonitoringValue = (
  value: unknown,
  options?: {
    depth?: number;
    stringLimit?: number;
  },
): unknown => {
  const depth = options?.depth ?? 0;
  const stringLimit = options?.stringLimit ?? 2000;
  if (depth > 4) return "[MaxDepth]";
  if (value == null) return value;
  if (typeof value === "string") {
    return value.length > stringLimit ? `${value.slice(0, stringLimit)}...[truncated]` : value;
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value instanceof Blob) return `[Blob ${value.type || "application/octet-stream"} ${value.size} bytes]`;
  if (value instanceof File) return `[File ${value.name} ${value.type || "application/octet-stream"} ${value.size} bytes]`;
  if (value instanceof FormData) {
    return Array.from(value.entries()).map(([key, entry]) => ({
      key,
      value: sanitizeMonitoringValue(entry, { depth: depth + 1, stringLimit }),
    }));
  }
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => sanitizeMonitoringValue(item, { depth: depth + 1, stringLimit }));
  }
  if (!isPlainObject(value)) return String(value);

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => {
      const normalizedKey = key.toLowerCase();
      if (REDACTED_KEYS.some((candidate) => normalizedKey.includes(candidate))) {
        return [key, "[REDACTED]"];
      }

      return [key, sanitizeMonitoringValue(entry, { depth: depth + 1, stringLimit })];
    }),
  );
};

const stringifyMonitoringValue = (value: unknown) => {
  if (value == null) return undefined;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const parseRequestBody = (body: BodyInit | null | undefined): unknown => {
  if (!body) return undefined;
  if (typeof body === "string") {
    try {
      return JSON.parse(body) as unknown;
    } catch {
      return body;
    }
  }
  if (body instanceof FormData) return body;
  if (body instanceof Blob || body instanceof File) return body;
  return `[Unsupported body type: ${body.constructor?.name ?? typeof body}]`;
};

const getSentry = async () => {
  if (!import.meta.env.PROD) return null;
  if (sentryModule) return sentryModule;
  if (sentryLoadAttempted) return null;
  sentryLoadAttempted = true;

  try {
    sentryModule = (await import("@sentry/react")) as unknown as SentryModuleLike;
    return sentryModule;
  } catch {
    return null;
  }
};

export const initMonitoring = async () => {
  if (!import.meta.env.PROD) return;
  if (monitoringInitialized) return;
  monitoringInitialized = true;

  LogRocket.init("2kvemt/akuna-pet");

  const Sentry = await getSentry();
  if (!Sentry) return;

  const dsn =
    import.meta.env.VITE_SENTRY_DSN ??
    "https://9571e9cf79f7a0ce7189498f61d69408@o4510839615455232.ingest.us.sentry.io/4510839616372736";

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
  });
};

export const setMonitoringUser = async (user: MonitoringUser | null) => {
  if (!import.meta.env.PROD) return;

  if (user) {
    try {
      LogRocket.identify(user.id, {
        email: user.email,
        name: user.name,
      });
      const Sentry = await getSentry();
      Sentry?.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
    } catch {
      // ignore
    }
  }
};

export const captureException = async (error: unknown, options?: CaptureOptions) => {
  if (!import.meta.env.PROD) return;

  try {
    (LogRocket as unknown as { captureException?: (e: unknown) => void }).captureException?.(error);
    const Sentry = await getSentry();
    console.log("Sentry", Sentry);
    if (!Sentry) return;

    if (options && "withScope" in Sentry && typeof Sentry.withScope === "function") {
      Sentry.withScope((scope) => {
        if (options.level) scope.setLevel(options.level);
        if (options.tags) {
          for (const [key, value] of Object.entries(options.tags)) scope.setTag(key, value);
        }
        if (options.extra) scope.setExtras(options.extra);
        if (options.fingerprint) scope.setFingerprint(options.fingerprint);
        Sentry.captureException(error);
      });
      return;
    }

    Sentry.captureException(error);
  } catch {
    // ignore
  }

};

export const captureTranscriptionInfo = async (params: {
  consultationId?: string | null;
  petId?: string | null;
  petName?: string | null;
  ownerName?: string | null;
  transcriptionText: string;
}) => {
  if (!import.meta.env.PROD) return;

  try {
    const trimmedText = params.transcriptionText.trim();
    if (!trimmedText) return;

    const Sentry = await getSentry();
    if (!Sentry || typeof Sentry.withScope !== "function" || typeof Sentry.captureMessage !== "function") {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setLevel("info");
      scope.setTag("feature", "consultation-transcription");
      scope.setExtras({
        consultationId: params.consultationId ?? null,
        petId: params.petId ?? null,
        petName: params.petName ?? null,
        ownerName: params.ownerName ?? null,
        transcriptionText: trimmedText,
      });

      Sentry.captureMessage?.("consultation_transcription_finished");
    });
  } catch {
    // ignore monitoring failures
  }
};

export const trackEdgeFunctionRequest = (url: string, init?: RequestInit) => {
  if (!import.meta.env.PROD) return;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const marker = "/functions/v1/";
    const markerIndex = parsedUrl.pathname.indexOf(marker);
    if (markerIndex === -1) return;

    const functionName = parsedUrl.pathname.slice(markerIndex + marker.length).split("/")[0];
    if (!functionName) return;

    const rawRequestBody = parseRequestBody(init?.body);
    const rawHeaders =
      init?.headers instanceof Headers
        ? Object.fromEntries(init.headers.entries())
        : Array.isArray(init?.headers)
          ? Object.fromEntries(init.headers)
          : init?.headers;

    const requestBody = sanitizeMonitoringValue(rawRequestBody, { stringLimit: LOGROCKET_STRING_LIMIT });
    const headers = sanitizeMonitoringValue(rawHeaders, { stringLimit: LOGROCKET_STRING_LIMIT });

    (
      LogRocket as unknown as {
        track?: (eventName: string, properties?: Record<string, unknown>) => void;
      }
    ).track?.("supabase_edge_function_request", {
      functionName,
      method: init?.method ?? "POST",
      requestBody: stringifyMonitoringValue(requestBody),
      headers: stringifyMonitoringValue(headers),
      url: parsedUrl.pathname,
    });
  } catch {
    // ignore monitoring failures
  }
};
